/**
 * SEVO Bundle Discount Calculator
 * Handles tiered bundle discounts with subscription stacking
 *
 * Discount Structure:
 * - 2 items: 5% bundle discount
 * - 3 items: 10% bundle discount
 * - 4+ items: 15% bundle discount
 * - Subscription: +10% additional discount
 *
 * Bundle discount applies to first order only.
 * Subscription discount continues on recurring orders.
 */

(function() {
  'use strict';

  window.SEVO = window.SEVO || {};

  const BundleDiscount = {
    // Tier configuration (can be overridden by theme settings)
    tiers: window.SEVO.bundleTiers || { 2: 5, 3: 10, 4: 15 },
    subscriptionBonus: window.SEVO.subscriptionBonus || 10,

    /**
     * Get bundle discount percentage based on item count
     * @param {number} itemCount - Number of items in bundle
     * @returns {number} Discount percentage (0, 5, 10, or 15)
     */
    getTierDiscount(itemCount) {
      if (itemCount >= 4) return this.tiers[4] || 15;
      if (itemCount >= 3) return this.tiers[3] || 10;
      if (itemCount >= 2) return this.tiers[2] || 5;
      return 0;
    },

    /**
     * Calculate total discount including bundle and subscription
     * @param {number} itemCount - Number of items
     * @param {boolean} isSubscription - Whether subscription is selected
     * @param {boolean} isFirstOrder - Whether this is customer's first bundle order
     * @returns {object} Discount breakdown
     */
    calculateDiscount(itemCount, isSubscription = false, isFirstOrder = true) {
      let bundlePercent = 0;
      let subscriptionPercent = 0;

      // Bundle discount only applies to first order
      if (isFirstOrder && itemCount >= 2) {
        bundlePercent = this.getTierDiscount(itemCount);
      }

      // Subscription discount always applies
      if (isSubscription) {
        subscriptionPercent = this.subscriptionBonus;
      }

      const totalPercent = bundlePercent + subscriptionPercent;

      return {
        bundlePercent,
        subscriptionPercent,
        totalPercent,
        isFirstOrder
      };
    },

    /**
     * Calculate savings and total for a given subtotal
     * @param {number} subtotalCents - Subtotal in cents
     * @param {number} itemCount - Number of items
     * @param {boolean} isSubscription - Whether subscription is selected
     * @param {boolean} isFirstOrder - Whether this is customer's first bundle order
     * @returns {object} Pricing breakdown
     */
    calculateTotal(subtotalCents, itemCount, isSubscription = false, isFirstOrder = true) {
      const discount = this.calculateDiscount(itemCount, isSubscription, isFirstOrder);

      const bundleSavings = Math.round(subtotalCents * (discount.bundlePercent / 100));
      const subscriptionSavings = Math.round(subtotalCents * (discount.subscriptionPercent / 100));
      const totalSavings = bundleSavings + subscriptionSavings;
      const total = subtotalCents - totalSavings;

      return {
        subtotal: subtotalCents,
        bundleSavings,
        subscriptionSavings,
        totalSavings,
        total,
        ...discount
      };
    },

    /**
     * Get message for next tier upsell
     * @param {number} itemCount - Current number of items
     * @returns {object|null} Next tier info or null if at max tier
     */
    getNextTierMessage(itemCount) {
      if (itemCount === 0) {
        return {
          itemsNeeded: 2,
          nextPercent: 5,
          message: 'Add 2 items for 5% off!'
        };
      }

      if (itemCount === 1) {
        return {
          itemsNeeded: 1,
          nextPercent: 5,
          message: 'Add 1 more item for 5% off!'
        };
      }

      if (itemCount === 2) {
        return {
          itemsNeeded: 1,
          currentPercent: 5,
          nextPercent: 10,
          message: 'Add 1 more for 10% off (currently 5%)'
        };
      }

      if (itemCount === 3) {
        return {
          itemsNeeded: 1,
          currentPercent: 10,
          nextPercent: 15,
          message: 'Add 1 more for 15% off (currently 10%)'
        };
      }

      // At max tier
      return null;
    },

    /**
     * Format money value from cents
     * @param {number} cents - Amount in cents
     * @returns {string} Formatted money string
     */
    formatMoney(cents) {
      const format = window.SEVO?.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return format
        .replace('{{amount}}', value)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100));
    },

    /**
     * Check if customer has used bundle discount before
     * @returns {boolean}
     */
    isFirstBundleOrder() {
      return !window.SEVO?.customer?.hasBundleOrder;
    },

    /**
     * Check if customer is logged in
     * @returns {boolean}
     */
    isLoggedIn() {
      return window.SEVO?.customer?.loggedIn || false;
    }
  };

  // Expose globally
  window.SEVO.BundleDiscount = BundleDiscount;
})();
